import React from 'react';
import './../ff001-starstruck-lines/ff001-starstruck.css';
import request from 'superagent';
import { generateWindowFromReelStrips, getSymbolCountsFromWindow, wrap } from '../../helpers';
import { Reel } from '../reel/reel';

type TColorCoinsScenarioId =
  | 'noColor'
  | 'greenOnly'
  | 'blueOnly'
  | 'redOnly'
  | 'greenBlue'
  | 'redGreen'
  | 'redBlue'
  | 'redGreenBlue'
  | 'extraPlayFinalReveal'
  | 'extraPlayGreenCollect'
  | 'redCollision'
  | 'redSpinAward';

type TFf034ScenarioV1 = {
  kind: 'ff034-color-coins';
  version: 1;
  reelIndexes?: number[];
  forceColoredCoinTrigger?: boolean;
  holdSpinScenarioId?: TColorCoinsScenarioId;
};

type TScenarioPreset = { _id: string; scenarios: TFf034ScenarioV1[] };
type TTriggerReelIndexes = { reelIndexes: number[]; error?: undefined } | { reelIndexes: []; error: string };

const scenarioIds: TColorCoinsScenarioId[] = [
  'noColor',
  'greenOnly',
  'blueOnly',
  'redOnly',
  'greenBlue',
  'redGreen',
  'redBlue',
  'redGreenBlue',
  'extraPlayFinalReveal',
  'extraPlayGreenCollect',
  'redCollision',
  'redSpinAward',
];

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const normalizeReelIndexes = (value: unknown, config: any): number[] => {
  const reelCount = Array.isArray(config?.reelsSetup) ? config.reelsSetup.length : 0;
  if (!Array.isArray(value)) {
    return Array.from({ length: reelCount }).map(() => 0);
  }
  return Array.from({ length: reelCount }).map((_, reelIndex) => {
    const reel = Array.isArray(config?.baseSpinsReels?.[reelIndex]) ? config.baseSpinsReels[reelIndex] : [];
    const raw = Number(value[reelIndex]);
    const index = Number.isFinite(raw) ? raw : 0;
    if (reel.length <= 0) {
      return Math.max(0, Math.floor(index));
    }
    const wrapped = wrap(Math.floor(index), reel);
    return wrapped < 0 ? reel.length + wrapped : wrapped;
  });
};

const getCoinTriggerStops = (config: any): TTriggerReelIndexes => {
  if (!Array.isArray(config?.baseSpinsReels)) {
    return { reelIndexes: [], error: 'Full base reels are unavailable in the loaded debug config.' };
  }
  const reels = config.baseSpinsReels as string[][];
  const coin = typeof config?.coin === 'string' ? config.coin : 'coin';
  const reelCount = Array.isArray(config?.reelsSetup) ? config.reelsSetup.length : reels.length;
  const reelIndexes: number[] = [];

  for (let reelIndex = 0; reelIndex < reelCount; reelIndex += 1) {
    const reel = reels[reelIndex];
    if (!Array.isArray(reel)) {
      return { reelIndexes: [], error: `Base reel ${reelIndex} is unavailable.` };
    }
    const coinIndex = reel.indexOf(coin);
    if (coinIndex < 0) {
      return { reelIndexes: [], error: `Base reel ${reelIndex} does not contain ${coin}.` };
    }
    reelIndexes.push(coinIndex);
  }

  return { reelIndexes };
};

const coinTriggerStops = (config: any) => {
  const result = getCoinTriggerStops(config);
  if (result.error) {
    throw new Error(result.error);
  }
  return result.reelIndexes;
};

const buildScenarioQueue = (
  config: any,
  scenarioId: TColorCoinsScenarioId,
  continuationCount: number,
  reelIndexes?: number[],
): TFf034ScenarioV1[] => {
  const continuation = Math.max(1, Math.min(continuationCount, 20));
  const triggerReelIndexes = reelIndexes?.length
    ? normalizeReelIndexes(reelIndexes, config)
    : coinTriggerStops(config);
  return [
    {
      kind: 'ff034-color-coins',
      version: 1,
      reelIndexes: triggerReelIndexes,
      forceColoredCoinTrigger: true,
      holdSpinScenarioId: scenarioId,
    },
    ...Array.from({ length: continuation }).map(() => ({
      kind: 'ff034-color-coins' as const,
      version: 1 as const,
      holdSpinScenarioId: scenarioId,
    })),
  ];
};

const labelForQueue = (queue: TFf034ScenarioV1[]) => {
  const first = queue[0];
  const id = first?.holdSpinScenarioId || queue.find((entry) => entry.holdSpinScenarioId)?.holdSpinScenarioId || 'custom';
  return `sc. ${id}, ${queue.length} play${queue.length === 1 ? '' : 's'}`;
};

const labelForWindow = (window: string[][]) => {
  const counts = getSymbolCountsFromWindow(window);
  const parts = Object.keys(counts)
    .sort()
    .map((symbol) => `${symbol}:${counts[symbol]}`);
  return parts.length ? parts.join('  ') : 'No visible symbols';
};

const copyText = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

const Model = (props: { modelId: string }) => {
  const [modelId] = React.useState(props.modelId);
  const [render, setRender] = React.useState(false);
  const [config, setConfig] = React.useState<any>(null);
  const [userPublicKey, setUserPublicKey] = React.useState('');
  const [scenarioId, setScenarioId] = React.useState<TColorCoinsScenarioId>('redGreenBlue');
  const [continuationCount, setContinuationCount] = React.useState(16);
  const [reelIndexes, setReelIndexes] = React.useState<number[]>([]);
  const [scenarios, setScenarios] = React.useState<TFf034ScenarioV1[]>([]);
  const [selectedScenario, setSelectedScenario] = React.useState('');
  const [scenarioList, setScenarioList] = React.useState<TScenarioPreset[]>([]);
  const [lastQueueResult, setLastQueueResult] = React.useState('');
  const triggerReelIndexes = React.useMemo(() => config ? getCoinTriggerStops(config) : { reelIndexes: [], error: 'Config is still loading.' }, [config]);
  const selectedReelIndexesText = reelIndexes.join(',');
  const visibleWindow = React.useMemo(() => {
    if (!config || reelIndexes.length === 0 || !Array.isArray(config?.baseSpinsReels)) { return []; }
    return generateWindowFromReelStrips({
      reels: Array.isArray(config?.reelsSetup) ? config.reelsSetup.length : reelIndexes.length,
      rows: Array.isArray(config?.reelsSetup) ? config.reelsSetup[0] : 3,
      reelIndexes,
      reelStrips: config.baseSpinsReels,
    });
  }, [config, reelIndexes]);
  const smokeCommand = [
    `MODEL_ID=${modelId}`,
    `FV_E2E_SCENARIO_ID=${scenarioId}`,
    selectedReelIndexesText ? `FV_E2E_FF034_REEL_INDEXES=${selectedReelIndexesText}` : '',
    'npm run test:e2e:scenario',
  ].filter(Boolean).join(' ');

  const changeUserPublicKey = (txt: string) => {
    setUserPublicKey(txt);
    localStorage.setItem('userid', txt);
  };

  const rebuildQueue = (
    nextScenarioId = scenarioId,
    nextContinuationCount = continuationCount,
    nextReelIndexes = reelIndexes,
  ) => {
    if (!config) { return; }
    try {
      setSelectedScenario('');
      const normalized = normalizeReelIndexes(nextReelIndexes, config);
      setReelIndexes(normalized);
      setScenarios(buildScenarioQueue(config, nextScenarioId, nextContinuationCount, normalized));
    } catch (e: any) {
      alert(e?.message || 'Unable to build ff034 scenario queue');
    }
  };

  const setTriggerReelIndex = (params: { reelIndex: number, dir?: 1 | -1, forcedIndex?: number }) => {
    if (!config) { return; }
    const { reelIndex, dir, forcedIndex } = params;
    const reels = Array.isArray(config?.baseSpinsReels?.[reelIndex]) ? config.baseSpinsReels[reelIndex] : [];
    if (reels.length === 0) { return; }
    const current = reelIndexes[reelIndex] || 0;
    const raw = (typeof forcedIndex === 'number') ? forcedIndex : current + (1 * (dir || 1));
    const wrapped = wrap(raw, reels);
    const nextValue = wrapped < 0 ? reels.length + wrapped : wrapped;
    const next = normalizeReelIndexes(
      reelIndexes.map((index, i) => i === reelIndex ? nextValue : index),
      config,
    );
    rebuildQueue(scenarioId, continuationCount, next);
  };

  const useCoinTriggerStops = () => {
    if (triggerReelIndexes.error) {
      alert(triggerReelIndexes.error);
      return;
    }
    rebuildQueue(scenarioId, continuationCount, triggerReelIndexes.reelIndexes);
  };

  const useScenarioId = (nextScenarioId: TColorCoinsScenarioId) => {
    setScenarioId(nextScenarioId);
    rebuildQueue(nextScenarioId, continuationCount);
  };

  const save = async () => {
    const payload = {
      scenarios,
      modelId,
      _id: selectedScenario === '' ? undefined : selectedScenario,
    };
    const res = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/upsert`).withCredentials().send(payload))?.body;
    if (!res.success) { return alert('Error saving scenario'); }
    console.log('Scenario saved');
  };

  const del = async () => {
    if (!window.confirm('Confirm deletion')) { return; }
    const payload = {
      scenarios,
      _id: selectedScenario,
    };
    const res = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/delete`).withCredentials().send(payload))?.body;
    if (!res.success) { return alert('Error deleting scenario'); }
    alert('Scenario deleted');
    window.location.reload();
  };

  const play = async () => {
    if (userPublicKey.trim() === '') { return alert('Please enter a user id'); }
    if (scenarios.length === 0) { return alert('Please build a scenario first'); }
    const payload = {
      scenarios,
      modelId,
      gameId: modelId,
      operatorUserId: userPublicKey,
      userPublicKey,
    };
    const res = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/play-user`).withCredentials().send(payload))?.body;
    if (!res.success) { return alert('Error saving scenario'); }
    setLastQueueResult(`Queued ${res.queueLength || scenarios.length} play${(res.queueLength || scenarios.length) === 1 ? '' : 's'} for ${userPublicKey}`);
    console.log('Scenario queued', res);
  };

  React.useEffect(() => {
    if (!config) { return; }
    try {
      const initialReelIndexes = triggerReelIndexes.error ? normalizeReelIndexes([], config) : triggerReelIndexes.reelIndexes;
      setReelIndexes(initialReelIndexes);
      setScenarios(buildScenarioQueue(config, scenarioId, continuationCount, initialReelIndexes));
    } catch (e: any) {
      alert(e?.message || 'Unable to build ff034 scenario queue');
    }
  }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const stored = scenarioList.find((it) => it._id === selectedScenario)?.scenarios;
    if (selectedScenario === '' || !stored) { return; }
    const next = clone(stored);
    setScenarios(next);
    if (next[0]?.reelIndexes) {
      setReelIndexes(normalizeReelIndexes(next[0].reelIndexes, config));
    }
    const storedScenarioId = next.find((entry) => entry.holdSpinScenarioId)?.holdSpinScenarioId;
    if (storedScenarioId) { setScenarioId(storedScenarioId); }
    setContinuationCount(Math.max(1, next.length - 1));
  }, [selectedScenario]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    localStorage.getItem('userid') && setUserPublicKey(`${localStorage.getItem('userid')}`);
    let req: request.SuperAgentRequest | undefined;
    req = request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/get-config`).withCredentials().send({ modelId });
    req.then(async (configRes) => {
      const nextConfig = configRes.body.config;
      setConfig(nextConfig);

      req = request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/get`).withCredentials().send({ modelId });
      req.then(async (scenariosRes) => {
        if (!scenariosRes.body.success) { return alert('Error loading scenarios'); }
        req = undefined;
        setScenarioList(scenariosRes.body.scenarios || []);
        setRender(true);
      }).catch((e) => console.error(e));
    }).catch((e) => console.error(e));
    return () => {
      try { req?.abort(); } catch (e) { console.log('Req aborted'); }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return !render ? <></> : (
    <div className="ff001-container">
      <h1>{modelId}</h1>
      <div className='scenario-presets'>
        <h3 style={{ marginBottom: 5 }}>Scenario presets</h3>
        <div className='ff001-scenario-select'>
          <select value={selectedScenario} onChange={(e) => setSelectedScenario(e.target.value)} >
            <option label={''} value={''} />
            {scenarioList.map((ps, i) => <option key={`ps-${i}`} value={`${ps._id}`} label={labelForQueue(ps.scenarios)} />)}
          </select>
        </div>
      </div>

      <div className='btn-freespins-group' style={{ flexWrap: 'wrap', gap: 8 }}>
        <select
          value={scenarioId}
          onChange={(e) => {
            const next = e.currentTarget.value as TColorCoinsScenarioId;
            useScenarioId(next);
          }}
        >
          {scenarioIds.map((id) => <option key={id} value={id}>{id}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Continuations
          <input
            type='number'
            min={1}
            max={20}
            value={continuationCount}
            onChange={(e) => {
              const next = Number(e.currentTarget.value) || 1;
              setContinuationCount(next);
              rebuildQueue(scenarioId, next);
            }}
          />
        </label>
      </div>

      <div style={{
        background: '#202020',
        border: '1px solid rgba(255,255,255,0.16)',
        borderRadius: 8,
        margin: '16px 0',
        maxWidth: 960,
        padding: 12,
      }}>
        <h3 style={{ margin: '0 0 10px' }}>Manual QA scenario</h3>
        <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginBottom: 10 }}>
          Pick the base reels for the trigger spin, choose the Color Coins path, enter a user public key, then Play.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {scenarioIds.map((id) => (
            <button
              key={`quick-${id}`}
              onClick={() => useScenarioId(id)}
              style={{
                background: id === scenarioId ? '#ffcf4f' : '#efefef',
                border: 'none',
                borderRadius: 4,
                color: '#111',
                cursor: 'pointer',
                fontWeight: 700,
                padding: '6px 8px',
              }}
              type='button'
            >
              {id}
            </button>
          ))}
        </div>
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>Selected reel indexes</div>
          <code style={{ background: '#111', borderRadius: 4, padding: '6px 8px' }}>
            {selectedReelIndexesText || '(none)'}
          </code>
          <button
            disabled={!!triggerReelIndexes.error}
            onClick={useCoinTriggerStops}
            style={{ cursor: triggerReelIndexes.error ? 'not-allowed' : 'pointer', padding: '6px 10px' }}
            type='button'
          >
            Use coin trigger
          </button>
        </div>
        {triggerReelIndexes.error && (
          <div style={{ color: '#ffb3b3', fontSize: 12, marginBottom: 8 }}>
            {triggerReelIndexes.error}
          </div>
        )}
        <div className={'reelContainer'} style={{ background: '#181818', borderRadius: 8, overflowX: 'auto', padding: 8 }}>
          {reelIndexes.map((ri, i) => (
            <Reel
              reelIndex={ri}
              setReelIndex={(props: { reelIndex: number, dir?: 1 | -1, forcedIndex?: number }) => setTriggerReelIndex(props)}
              reelStrips={config.baseSpinsReels[i]}
              index={i}
              rows={config.reelsSetup[i]}
              key={`ff034-trigger-reel-${i}`}
            />
          ))}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginTop: 8 }}>
          {labelForWindow(visibleWindow)}
        </div>
      </div>

      <div style={{
        background: '#202020',
        border: '1px solid rgba(255,255,255,0.16)',
        borderRadius: 8,
        margin: '16px 0',
        maxWidth: 960,
        padding: 12,
      }}>
        <h3 style={{ margin: '0 0 10px' }}>Playwright smoke</h3>
        <div style={{ display: 'grid', gap: 10 }}>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginBottom: 4 }}>FF034 trigger reel indexes</div>
            <div style={{ alignItems: 'center', display: 'flex', gap: 8 }}>
              <code style={{ background: '#111', borderRadius: 4, padding: '6px 8px' }}>
                {selectedReelIndexesText || '(unavailable)'}
              </code>
              <button
                disabled={!selectedReelIndexesText}
                onClick={() => copyText(selectedReelIndexesText)}
                style={{ cursor: selectedReelIndexesText ? 'pointer' : 'not-allowed', padding: '6px 10px' }}
                type='button'
              >
                Copy indexes
              </button>
            </div>
            {triggerReelIndexes.error && (
              <div style={{ color: '#ffb3b3', fontSize: 12, marginTop: 6 }}>
                {triggerReelIndexes.error}
              </div>
            )}
          </div>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginBottom: 4 }}>Smoke command</div>
            <div style={{ alignItems: 'flex-start', display: 'flex', gap: 8 }}>
              <code style={{
                background: '#111',
                borderRadius: 4,
                flex: 1,
                lineHeight: 1.5,
                padding: '6px 8px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {smokeCommand}
              </code>
              <button
                disabled={!selectedReelIndexesText}
                onClick={() => copyText(smokeCommand)}
                style={{ cursor: selectedReelIndexesText ? 'pointer' : 'not-allowed', padding: '6px 10px' }}
                type='button'
              >
                Copy command
              </button>
            </div>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginTop: 6 }}>
              Requires `FV_RGS_API_KEY` and local RGS/casino/client services in your shell.
            </div>
          </div>
        </div>
      </div>

      <pre style={{ maxWidth: 960, whiteSpace: 'pre-wrap', fontSize: 11 }}>
        {JSON.stringify(scenarios, null, 2)}
      </pre>

      <div className='ff001-sep'>&nbsp;</div>
      <div className={`ff001-userid`}>
        <h3 style={{ marginRight: 8 }}>User Public Key</h3>
        <div>
          <input type={'text'} value={userPublicKey} onChange={(e) => changeUserPublicKey(e.currentTarget.value)} />
        </div>
      </div>
      <div style={{ fontSize: 8 }}>To find it: Go to the game, network tab, filter by "init", under Preview, user then id </div>

      <div className='model-buttons'>
        <div className={'btn-save'} onClick={() => rebuildQueue(scenarioId, continuationCount)}>New</div>
        <div className={'btn-save'} onClick={() => save()}>Save</div>
        {selectedScenario !== '' && <div className={'btn-save'} onClick={() => del()}>Delete</div>}
        <div className={'btn-save btn-play'} onClick={() => play()}>Play</div>
      </div>
      {lastQueueResult && (
        <div style={{ color: '#adffce', fontSize: 13, fontWeight: 700, marginTop: -12 }}>
          {lastQueueResult}
        </div>
      )}
    </div>
  );
};

export default Model;
